import { DateTime } from 'luxon';
import { MESSAGE_TYPE } from '../../redux/api/orders/orders.model';
import { Trip } from '../../redux/api/trips/trips.model';

export type ShareTripSMSConstructorParams = {
  messageType: MESSAGE_TYPE;
  company: { company_name: string; short_offset: string };
  use24HourClock: boolean;
  trip: Trip;
  order?: {
    readable_id: string | number;
    customer_order_number?: string;
  } | null;
  ltl_enabled: boolean;
  useCustomerOrderNumber: boolean;
};

export default class ShareTripSMS {
  public message: string;

  constructor(params: ShareTripSMSConstructorParams) {
    const tripIdentifier = this.buildTripIdentifier(params);
    const locationDateTimeString = this.buildLocationDateTimeString(params);

    switch (params.messageType) {
      case MESSAGE_TYPE.SHARE:
        this.message = this.buildNewLoadMessage({
          ...params,
          tripIdentifier,
          locationDateTimeString
        });
        break;
      case MESSAGE_TYPE.UPDATE:
        this.message = this.buildUpdateMessage({
          ...params,
          tripIdentifier
        });
        break;
      case MESSAGE_TYPE.UNASSIGNED:
        this.message = this.buildUnassignedMessage({
          ...params,
          tripIdentifier
        });
    }
  }

  private buildTripIdentifier(params: ShareTripSMSConstructorParams): string {
    const { ltl_enabled, order, trip, useCustomerOrderNumber } = params;

    if (!ltl_enabled && order) {
      if (useCustomerOrderNumber && order.customer_order_number) {
        return `Customer Order # ${order.customer_order_number}`;
      }
      return `Trip #${order.readable_id}-${trip.sort_order + 1}`;
    }
    return `Trip #${trip.readable_id}`;
  }

  private buildLocationDateTimeString(params: ShareTripSMSConstructorParams): string {
    const { use24HourClock, trip } = params;
    const hourFormat = use24HourClock ? DateTime.TIME_24_SIMPLE : DateTime.TIME_SIMPLE;

    const firstStop = trip.stops?.length ? trip.stops[0] : undefined;

    let locationDateTimeString = '';
    if (firstStop?.location) {
      locationDateTimeString += `${firstStop.location.full_address}, `;
    }
    if (firstStop?.start_date) {
      locationDateTimeString += `${DateTime.fromISO(firstStop.start_date).toFormat('LL/dd')}`;
    }
    if (firstStop?.start_time) {
      locationDateTimeString += ` ${DateTime.fromFormat(
        firstStop.start_time,
        'HH:mm:ss'
      ).toLocaleString(hourFormat)}`;
    }
    return locationDateTimeString;
  }

  private buildNewLoadMessage(
    params: ShareTripSMSConstructorParams & {
      tripIdentifier: string;
      locationDateTimeString: string;
    }
  ): string {
    return `${params.company.company_name} sent you a trip.\n${params.tripIdentifier}\n${params.locationDateTimeString}\nCheck dispatch link for trip details.`;
  }

  private buildUpdateMessage(
    params: ShareTripSMSConstructorParams & {
      tripIdentifier: string;
    }
  ): string {
    return `UPDATE from ${params.company.company_name}.\nNew changes made to ${params.tripIdentifier}.\nPlease check driver link for full details.`;
  }

  private buildUnassignedMessage(
    params: ShareTripSMSConstructorParams & {
      tripIdentifier: string;
    }
  ): string {
    return `ALERT: ${params.tripIdentifier}.\nUpdate: You have been UNASSIGNED from this trip. Please check the app for any new trip assignments.`;
  }

  getMessage(): string {
    return this.message;
  }

  toString(): string {
    return this.message;
  }
}
